/* ── LC PDP v1 — mobile product page enhancements ──
 * 1. Size-first ATC: when no size is selected, ATC taps scroll to the size
 *    selector and pulse it (no dead disabled button, no generic toast).
 * 2. "Select Size" labels replace "Unavailable" while nothing is selected.
 * 3. Real delivery estimate widget (pincode → API, honest ranges).
 * 4. Gallery "1/9" fraction counter on mobile.
 * 5. Header shrinks after scrolling.
 * 6. Sticky ATC hides near the footer; WhatsApp float stays clear of it.
 * 7. Lightweight, deduplicated conversion events (no PII):
 *    add_to_cart on real cart adds, begin_checkout when Fastrr opens.
 */
(function () {
  'use strict';

  var API = (window.__lc && window.__lc.api) || 'https://shopify-connect-manavlakhina.replit.app/api';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- helpers ---------- */
  function mainProduct() { return document.querySelector("product-info[id^='MainProduct']"); }
  function hasSelectedVariant() {
    // product-info.js keeps `currentVariant` on the element; with
    // disable_selected_variant_default it is null until the shopper picks a
    // size. The SSR [data-selected-variant] JSON is NOT cleared, so we must
    // read the live property, not the markup.
    var mp = mainProduct();
    if (!mp) return true;
    if ('currentVariant' in mp) return !!mp.currentVariant;
    return true; // element not initialised yet — don't interfere
  }

  function pulseSizeSelector() {
    var mp = mainProduct();
    if (!mp) return;
    var picker = mp.querySelector('variant-picker');
    if (!picker) return;
    var header = document.querySelector('.m-header');
    var offset = (header ? header.offsetHeight : 60) + 12;
    var y = picker.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    picker.classList.remove('lc-pulse');
    // restart animation
    void picker.offsetWidth;
    picker.classList.add('lc-pulse');
    setTimeout(function () { picker.classList.remove('lc-pulse'); }, 1800);
  }

  /* ---------- 1+2: size-first add to cart ---------- */
  function initSizeGuard() {
    var mp = mainProduct();
    if (!mp) return;

    // Replace "Unavailable" with "Select Size" while nothing is selected
    function relabel() {
      if (hasSelectedVariant()) return;
      document.querySelectorAll('.m-product-form--main [name="add"], .sticky-atc-form [name="add"]').forEach(function (btn) {
        var span = btn.querySelector('.m-add-to-cart--text');
        if (span && /unavailable/i.test(span.textContent)) span.textContent = 'Select Size';
      });
    }
    relabel();
    var mo = new MutationObserver(function () { relabel(); });
    document.querySelectorAll('.m-product-form--main [name="add"], .sticky-atc-form [name="add"]').forEach(function (btn) {
      mo.observe(btn, { childList: true, subtree: true, characterData: true });
    });

    // Disabled buttons swallow clicks — catch taps on the wrapper instead
    document.addEventListener('click', function (e) {
      var wrapper = e.target.closest('.m-product-form__wrapper, .m-sticky-addtocart');
      if (!wrapper) return;
      var btn = wrapper.querySelector('[name="add"]');
      if (!btn || !btn.disabled) return;
      if (hasSelectedVariant()) return; // genuinely sold out — leave it alone
      e.preventDefault();
      pulseSizeSelector();
    }, true);
  }

  /* ---------- 3: delivery estimate ---------- */
  function initDelivery() {
    var box = document.querySelector('[data-lc-delivery]');
    if (!box) return;
    var input = box.querySelector('[data-lc-pincode]');
    var btn = box.querySelector('[data-lc-pincheck]');
    var result = box.querySelector('[data-lc-delivery-result]');
    if (!input || !btn || !result) return;

    function span(cls, text) {
      var s = document.createElement('span');
      s.className = cls;
      s.textContent = text;
      return s;
    }

    /* nodes: array of DOM nodes (or a string for plain error text).
       Never uses innerHTML — city/state come from a third-party API. */
    function render(nodes, isError) {
      result.textContent = '';
      if (typeof nodes === 'string') {
        result.appendChild(document.createTextNode(nodes));
      } else {
        nodes.forEach(function (n) { result.appendChild(n); });
      }
      result.classList.toggle('is-error', !!isError);
      result.hidden = false;
    }

    function check() {
      var pin = (input.value || '').trim();
      if (!/^[1-9][0-9]{5}$/.test(pin)) {
        render('Please enter a valid 6-digit pincode.', true);
        return;
      }
      btn.disabled = true;
      btn.textContent = '…';
      fetch(API + '/delivery-estimate?pincode=' + pin)
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          btn.disabled = false;
          btn.textContent = 'Check';
          if (!res.ok || !res.j.ok) {
            render((res.j && res.j.error) || 'Could not check this pincode right now.', true);
            return;
          }
          var d = res.j;
          var lines = [];
          if (d.city) {
            var place = d.city + (d.state ? ', ' + d.state : '');
            lines.push(span('lc-delivery__place', 'Delivering to ' + place + ' — ' + d.pincode));
          }
          var eta = span('lc-delivery__eta', 'Estimated delivery: ');
          var strong = document.createElement('strong');
          strong.textContent = d.estimatedDelivery.from + ' – ' + d.estimatedDelivery.to;
          eta.appendChild(strong);
          lines.push(eta);
          var dispatchText = d.dispatchDays.min === d.dispatchDays.max
            ? d.dispatchDays.min + ' days'
            : d.dispatchDays.min + '–' + d.dispatchDays.max + ' working days';
          lines.push(span('lc-delivery__note', 'Stitched to order in ' + dispatchText + ', then shipped to you.'));
          render(lines, false);
          try { localStorage.setItem('lc_pincode', pin); } catch (e) {}
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = 'Check';
          render('Could not check this pincode right now.', true);
        });
    }

    btn.addEventListener('click', check);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); check(); } });

    try {
      var saved = localStorage.getItem('lc_pincode');
      if (saved && /^[1-9][0-9]{5}$/.test(saved)) {
        input.value = saved;
        check();
      }
    } catch (e) {}
  }

  /* ---------- 4: gallery fraction counter ---------- */
  function initGalleryCounter() {
    if (window.matchMedia('(min-width: 768px)').matches) return;
    var media = document.querySelector('.m-product-media--slider .swiper, .m-product-media--slider.swiper, [data-product-single-media-group].swiper');
    if (!media) media = document.querySelector('.m-product-media--slider');
    if (!media) return;

    var total = media.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length ||
      media.querySelectorAll('.m-product-media--item').length;
    if (total < 2) return;

    var counter = document.createElement('div');
    counter.className = 'lc-media-counter';
    counter.textContent = '1/' + total;
    var host = media.closest('.m-product-media') || media;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(counter);

    function bind() {
      var sw = media.swiper || (media.querySelector('.swiper') && media.querySelector('.swiper').swiper);
      if (sw && sw.on) {
        sw.on('slideChange', function () {
          counter.textContent = (sw.realIndex + 1) + '/' + total;
        });
        return true;
      }
      return false;
    }
    if (!bind()) {
      var tries = 0;
      var iv = setInterval(function () {
        if (bind() || ++tries > 20) clearInterval(iv);
      }, 500);
    }
  }

  /* ---------- 5: shrink header on scroll ---------- */
  function initHeaderShrink() {
    var last = 0;
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.pageYOffset;
        if (y > 90 && last <= 90) document.documentElement.classList.add('lc-header-shrunk');
        else if (y <= 90 && last > 90) document.documentElement.classList.remove('lc-header-shrunk');
        last = y;
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- 6: sticky ATC vs footer + WhatsApp float ---------- */
  function initStickyRules() {
    var footer = document.querySelector('.m-footer, footer');
    if (!footer || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        document.documentElement.classList.toggle('lc-near-footer', entry.isIntersecting);
      });
    }, { rootMargin: '0px 0px -40% 0px', threshold: 0 });
    io.observe(footer);
  }

  /* ---------- 7: conversion events (deduplicated, no PII) ---------- */
  function track(eventName, params, dedupeKey) {
    try {
      if (dedupeKey) {
        var k = 'lc_evt_' + dedupeKey;
        if (sessionStorage.getItem(k)) return;
        sessionStorage.setItem(k, '1');
      }
      if (typeof window.gtag === 'function') window.gtag('event', eventName, params || {});
    } catch (e) {}
  }

  function productMeta() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (m) return { item_id: String(m.id), item_name: m.type ? undefined : undefined, id: String(m.id) };
    } catch (e) {}
    return null;
  }

  function initTracking() {
    var meta = productMeta();
    var pid = meta ? meta.item_id : (location.pathname.split('/products/')[1] || '').split('?')[0];

    // add_to_cart: hook real cart adds (theme uses fetch to /cart/add)
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (url.indexOf('/cart/add') !== -1) {
        p.then(function (resp) {
          if (resp.ok) track('add_to_cart', { items: pid ? [{ item_id: pid }] : [] });
        }).catch(function () {});
      }
      return p;
    };

    // begin_checkout: Fastrr replaces Shopify checkout with a popup — detect it
    var seen = false;
    var mo = new MutationObserver(function (muts) {
      if (seen) return;
      for (var i = 0; i < muts.length; i++) {
        var nodes = muts[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (n.nodeType !== 1) continue;
          var idc = ((n.id || '') + ' ' + (n.className && n.className.baseVal ? '' : n.className || '')).toLowerCase();
          var src = (n.tagName === 'IFRAME' && n.src) ? n.src.toLowerCase() : '';
          if (idc.indexOf('fastrr') !== -1 || idc.indexOf('pickrr') !== -1 || idc.indexOf('shiprocket') !== -1 ||
              src.indexOf('fastrr') !== -1 || src.indexOf('pickrr') !== -1 || src.indexOf('shiprocket') !== -1 ||
              (n.querySelector && n.querySelector('iframe[src*="fastrr"], iframe[src*="pickrr"], iframe[src*="shiprocket"]'))) {
            seen = true;
            track('begin_checkout', { items: pid ? [{ item_id: pid }] : [] }, 'bc_' + pid);
            mo.disconnect();
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  ready(function () {
    if (!document.querySelector("product-info[id^='MainProduct']")) {
      // Not a product page — only header shrink applies globally
      initHeaderShrink();
      return;
    }
    initSizeGuard();
    initDelivery();
    initGalleryCounter();
    initHeaderShrink();
    initStickyRules();
    initTracking();
  });
})();
